import { useState } from 'react'
import { useForm, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Shield } from 'lucide-react'

interface Invite {
  token: string
  name: string
  phone_hint: string
  role: string
  expires_at: string | null
}

interface Business {
  id: number
  name: string
}

interface AcceptInviteProps {
  invite: Invite
  business: Business
  step: 'request_otp' | 'verify_otp' | 'create_account'
  errors?: Record<string, string>
}

export default function AcceptInvite({
  invite,
  business,
  step,
  errors = {},
}: AcceptInviteProps) {
  const [code, setCode] = useState('')
  const [processingOtp, setProcessingOtp] = useState(false)

  const { data, setData, processing } = useForm({
    name: invite.name || '',
  })

  const handleRequestOtp = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessingOtp(true)
    router.post(`/invite/${invite.token}/request_otp`, {}, {
      onFinish: () => setProcessingOtp(false),
    })
  }

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessingOtp(true)
    router.post(`/invite/${invite.token}/verify_otp`, { code }, {
      onFinish: () => setProcessingOtp(false),
    })
  }

  const handleCreateAccount = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(`/invite/${invite.token}/accept`, {
      user: data,
    })
  }

  if (step === 'request_otp') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Verificar invitación</CardTitle>
            <CardDescription>
              Para unirte a <span className="font-medium">{business.name}</span>, enviaremos un código a tu WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div className="space-y-2">
                {errors.phone && (
                  <p className="text-sm text-destructive">{errors.phone}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Número: termina en {invite.phone_hint.slice(-4)}
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={processingOtp}>
                {processingOtp ? 'Enviando...' : 'Enviar código'}
              </Button>
            </form>

            <div className="mt-6 text-center text-xs text-muted-foreground">
              <p>Invitación para: {invite.name}</p>
              <p>Rol: {invite.role === 'admin' ? 'Administrador' : 'Personal'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (step === 'verify_otp') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Ingresa el código</CardTitle>
            <CardDescription>
              Enviamos un código a tu WhatsApp (termina en {invite.phone_hint.slice(-4)})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Código</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                {errors.code && (
                  <p className="text-sm text-destructive">{errors.code}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={processingOtp}>
                {processingOtp ? 'Verificando...' : 'Verificar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Únete a {business.name}</CardTitle>
          <CardDescription>
            Crea tu cuenta para unirte como{' '}
            <span className="font-medium">
              {invite.role === 'admin' ? 'Administrador' : 'Personal'}
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Tu nombre</Label>
              <Input
                id="name"
                placeholder="Tu nombre completo"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                required
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={processing}>
              {processing ? 'Creando cuenta...' : 'Crear cuenta y unirme'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
